import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import Login from "./components/Login";
import Dashboard from "./components/Dashboard";
import LeaderboardsPage from "./components/LeaderboardsPage";
import { authService } from "./services/authService";

const AppContainer = styled.div`
    min-height: 100vh;
    background-color: #f5f5f5;
`;

const Header = styled.header`
    background-color: #2c3e50;
    color: white;
    padding: 1rem 2rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
        padding: 1rem;
        flex-direction: column;
        align-items: stretch;
    }
`;

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 2rem;
    
    @media (max-width: 768px) {
        justify-content: center;
        gap: 1rem;
        margin-bottom: 1rem;
    }
`;

const Title = styled.h1`
    margin: 0;
    font-size: 1.5rem;
    font-weight: 600;
    
    @media (max-width: 768px) {
        font-size: 1.2rem;
        text-align: center;
    }
`;

const Navigation = styled.nav`
    display: flex;
    align-items: center;
    gap: 1.5rem;
    
    @media (max-width: 768px) {
        justify-content: center;
        gap: 1rem;
    }
`;

const NavLink = styled(Link)`
    color: #bdc3c7;
    text-decoration: none;
    font-size: 0.95rem;
    font-weight: 500;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    transition: all 0.2s ease;
    
    @media (max-width: 768px) {
        font-size: 0.9rem;
        padding: 0.4rem 0.8rem;
    }

    &:hover {
        color: white;
        background-color: rgba(255, 255, 255, 0.1);
    }

    &.active {
        color: white;
        background-color: rgba(255, 255, 255, 0.2);
    }
`;

const UserInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    
    @media (max-width: 768px) {
        justify-content: center;
        flex-wrap: wrap;
        gap: 0.5rem;
    }
`;

const UserName = styled.span`
    font-size: 0.9rem;
    color: #bdc3c7;
    
    @media (max-width: 768px) {
        font-size: 0.8rem;
        text-align: center;
    }
`;

const LogoutButton = styled.button`
    background-color: #e74c3c;
    color: white;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;

    &:hover {
        background-color: #c0392b;
    }
    
    @media (max-width: 768px) {
        padding: 0.4rem 0.8rem;
        font-size: 0.8rem;
    }
`;

function App() {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [currentUser, setCurrentUser] = useState(null);
    const location = useLocation();

    useEffect(() => {
        // Check if user is already authenticated
        const token = authService.getToken();
        const user = authService.getUser();
        if (token && user) {
            setIsAuthenticated(true);
            setCurrentUser(user);
        }
        setIsLoading(false);
    }, []);

    const handleLogin = (user) => {
        setIsAuthenticated(true);
        setCurrentUser(user);
    };

    const handleLogout = () => {
        authService.logout();
        setIsAuthenticated(false);
        setCurrentUser(null);
    };

    if (isLoading) {
        return (
            <AppContainer>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100vh",
                    }}
                >
                    Loading...
                </div>
            </AppContainer>
        );
    }

    return (
        <AppContainer>
            {isAuthenticated && (
                <Header>
                    <HeaderLeft>
                        <Title>Visual Search Evaluator</Title>
                        <Navigation>
                            <NavLink 
                                to="/" 
                                className={location.pathname === "/" ? "active" : ""}
                            >
                                Dashboard
                            </NavLink>
                            <NavLink 
                                to="/leaderboards" 
                                className={location.pathname === "/leaderboards" ? "active" : ""}
                            >
                                Leaderboards
                            </NavLink>
                        </Navigation>
                    </HeaderLeft>
                    <UserInfo>
                        {currentUser && (
                            <UserName>
                                Logged in as: {currentUser.display_name}
                            </UserName>
                        )}
                        <LogoutButton onClick={handleLogout}>Logout</LogoutButton>
                    </UserInfo>
                </Header>
            )}

            <Routes>
                <Route
                    path="/login"
                    element={
                        isAuthenticated ? (
                            <Navigate to="/" replace />
                        ) : (
                            <Login onLogin={handleLogin} />
                        )
                    }
                />
                <Route
                    path="/"
                    element={
                        isAuthenticated ? (
                            <Dashboard currentUser={currentUser} />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />
                <Route
                    path="/leaderboards"
                    element={
                        isAuthenticated ? (
                            <LeaderboardsPage currentUser={currentUser} />
                        ) : (
                            <Navigate to="/login" replace />
                        )
                    }
                />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </AppContainer>
    );
}

export default App;
